// plugins/axios.client.ts
import axios from 'axios';
import { defineNuxtPlugin, useRuntimeConfig } from '#app';

export default defineNuxtPlugin((nuxtApp) => {
    const config = useRuntimeConfig();

    // 1️⃣ Create your axios instance
    const api = axios.create({
        baseURL: config.public.API_ENV,
    });

    // 2️⃣ Grab router, store and echo off the Vue app
    const { $echo, $removeAuthToken, $errortoast } = nuxtApp;
    const router = nuxtApp.vueApp.config.globalProperties.$router;


    // 3️⃣ Request interceptor
    api.interceptors.request.use((req) => {
        const url = req.url || '';

        // only append lang if the path matches
        const allowed = ['/analysis', 'messages', 'replies', 'ai/questions'];
        // Analysis export POSTs omit `lang` here on purpose; the client sends `export_lang` (see AnalysisTaskExportData).
        if (
            allowed.some(p => url.includes(p)) &&
            !url.includes('/export') &&
            !url.includes('noLang')
        ) {
            const store  = nuxtApp.$vuex;
            const taskSlug = router.currentRoute.value.params.taskSlug;
            if (taskSlug) {
                const lang = store.getters['webGeneral/getPreferredLanguage'](taskSlug);
                if (lang) {
                    req.params = { ...req.params, lang };
                }
            }
        }

        // add the Pusher socket‐id if Echo is available
        if ($echo) {
            req.headers = req.headers || {};
            req.headers['X-Socket-ID'] = $echo.socketId();
        }

        return req;
    }, (error) => Promise.reject(error));

    
    // 4️⃣ Response / error interceptor
    api.interceptors.response.use(
        (res) => res,
        (err) => {
            const url  = err.config?.url || '';
            const code = err.response?.status;

            const isExportPage     = url.includes('/export-requests/');
            const isVideoCallPub   = url.includes('public') && url.includes('video-call');
            const ignoreError      = code === 422 || isExportPage || isVideoCallPub || err.config?.handleErrorLocally;

            if (code === 403 && $errortoast) {
                $errortoast({
                    title: 'Unauthorised',
                    text:  'You do not have permission to perform that action.'
                });
            }

            if (!ignoreError) {
                // Let the caller handle it (or you can rethrow a Nuxt error)
                return Promise.reject(err);
            }

            // for ignored errors, still reject so callers can catch if they want
            return Promise.reject(err);
        }
    );

    // 5️⃣ Auth‐token helpers
    function removeAuthToken() {
        delete api.defaults.headers.common.Authorization;
        localStorage.removeItem('userToken');
    }

    function setAuthToken(token) {
        if (token) {
            api.defaults.headers.common.Authorization = `Bearer ${token}`;
            localStorage.setItem('userToken', token);
        } else {
            removeAuthToken();
        }
    }

    function logout() {
        removeAuthToken();
        // …any other logout cleanup…
    }

    // 6️⃣ Initialise from storage
    setAuthToken(localStorage.getItem('userToken') || undefined);

    nuxtApp.vueApp.config.globalProperties.$axios = api;

    if (nuxtApp.$vuex) {
        nuxtApp.$vuex.$axios = api;
    }

    // 7️⃣ Inject into the Nuxt app
    nuxtApp.provide('axios', api);
    nuxtApp.provide('setAuthToken', setAuthToken);
    nuxtApp.provide('removeAuthToken', removeAuthToken);
    nuxtApp.provide('logout', logout);
});
